import { type } from 'arktype';
import {
	experienceAccessLabelSchema,
	experienceDurationTypeSchema,
	experienceFormatSchema,
	experienceSubtypeSchema,
	intakeMetaSchema,
} from '../experiences';
import { createPaginatedResponseSchema } from '../shared/pagination';
import { ageRangeSchema } from '../users';

/**
 * El paso del asistente en el que se quedó el borrador (documento 03b, §7.1: ocho pasos —
 * 1 categoría … 8 resumen y envío).
 */
export const draftStepSchema = type('1 <= number.integer <= 8');

export type DraftStep = typeof draftStepSchema.infer;

/**
 * El borrador como payload PARCIAL de `createExperienceSchema` (decisión 6,
 * decisiones.md: responde D-19 del documento 03a — el asistente no guardaba nada y se
 * perdían los ocho pasos si se cerraba la pestaña). Se escribe a mano en vez de derivarlo
 * con `.partial()` sobre `createExperienceSchema`: ese contrato lleva un `.narrow()`
 * (CL-5) que no debe aplicarse a un guardado intermedio — un borrador a medio rellenar
 * puede tener, legítimamente, una fecha de fin sin fecha de inicio todavía. La validación
 * completa de CL-4/CL-5/RN-7 se aplica solo al enviar de verdad, contra
 * `createExperienceSchema`.
 */
export const experienceDraftPayloadSchema = type({
	'accessLabel?': experienceAccessLabelSchema,
	'ageSegments?': ageRangeSchema.array(),
	'capacity?': 'number.integer >= 0 | null',
	'categoryId?': 'string.uuid',
	'description?': 'string',
	'deTemporada?': 'boolean',
	'durationType?': experienceDurationTypeSchema,
	'endDate?': 'string.date.iso',
	'format?': experienceFormatSchema,
	'intakeMeta?': intakeMetaSchema,
	'locationId?': 'string.uuid',
	'newcomerReservePct?': 'number.integer >= 0',
	'nicheIds?': 'string.uuid[]',
	'priceCents?': 'number.integer >= 0',
	'startDate?': 'string.date.iso',
	'subtypes?': experienceSubtypeSchema.array(),
	'title?': 'string',
	'whatsappGroupUrl?': 'string',
}).onUndeclaredKey('delete');

export type ExperienceDraftPayload = typeof experienceDraftPayloadSchema.infer;

export const experienceDraftResponseSchema = type({
	createdAt: 'string.date.iso',
	// Documento 03c: cuándo caduca el borrador (columna `expiresAt`, `DEFAULT` a 90 días).
	expiresAt: 'string.date.iso',
	id: 'string.uuid',
	payload: experienceDraftPayloadSchema,
	step: draftStepSchema,
	updatedAt: 'string.date.iso',
	userId: 'string.uuid',
}).onUndeclaredKey('delete');

export type ExperienceDraftResponse = typeof experienceDraftResponseSchema.infer;

/** `POST /experience-drafts` (decisión propia, decisiones.md: ruta kebab-case — ver ADR-0018). */
export const createExperienceDraftSchema = type({
	'payload?': experienceDraftPayloadSchema,
	'step?': draftStepSchema,
}).onUndeclaredKey('delete');

export type CreateExperienceDraft = typeof createExperienceDraftSchema.infer;

// Misma forma que la creación: guardar un borrador es siempre un reemplazo del payload y
// del paso, nunca un parcheo campo a campo — el propio asistente manda el estado completo
// de la pantalla en la que está en cada guardado automático.
export const updateExperienceDraftSchema = createExperienceDraftSchema;

export type UpdateExperienceDraft = typeof updateExperienceDraftSchema.infer;

export const experienceDraftPaginatedResponseSchema = createPaginatedResponseSchema(experienceDraftResponseSchema);

export type ExperienceDraftPaginatedResponse = typeof experienceDraftPaginatedResponseSchema.infer;
